import Big from 'big.js';
import { format } from 'd3-format';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIConfig } from 'src/config';
import { COST_BENEFIT_STATE_ICONS, INHIBIT_CARBON_DATA_VIEWS, PLATFORM_MODE_REBATE } from 'src/util/constants';
import { costBenefitLabel, netValueDiffState } from 'src/util/counterfactual';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { valuePerUnit } from 'src/util/i18n/energyPrice';
import { i18nDecimalFormat } from 'src/util/i18n/handler';

import { UNIT_CARBON_TOOLTIP_FORMAT } from './chartConstants';
import { formatCarbonEmission } from '../helpers/common';

const Div = styled.div`
  border-${(props) => props.align}:
    .25rem solid ${(props) => (props.category === 'imports' ? '#e6c644' : '#3bb667')};
    margin-${(props) => props.align}: .5rem;
    padding-${(props) => props.align}: 1rem;
    text-align: ${(props) => props.align};
`;

const Title = styled.h3.attrs({
  className: 'text-uppercase',
})`
  font-size: .8rem;
  color: #adb5bd;
`;

/** The styled component for cost benefit text. */
const CostBenefitText = styled.span.attrs({
  className: 'ms-2',
})`
font-size: 0.8rem;
`;

/**
 * Transforms a big number into a string carbon value with its unit label
 * @param {Big} carbonValue
 * @returns {string} - carbon data i.e 373.334kg CO2-e
 */
export const getCarbonData = (carbonValue) => {
  const finalValues = formatCarbonEmission(carbonValue);
  const { value: finalValue, label } = finalValues;
  return `${format(UNIT_CARBON_TOOLTIP_FORMAT)(finalValue)}${label}`;
};

const inhibitCarbonDataViews = APIConfig()?.feature(INHIBIT_CARBON_DATA_VIEWS);
/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - ChartSummary component
 */
function ChartSummary(props) {
  const {
    align, carbonData, category, counterfactual, mode,
    rebatedEnergy, hasCounterfactual, tradedValue, tradedEnergy,
    untradedEnergy, untradedValue,
  } = props;

  const intl = useIntl();

  // i18n messages
  const energyImportsLabel = intl.formatMessage({
    id: 'chart.chart_summary.energy_imports.label',
    defaultMessage: 'Energy Imports',
  });
  const energyExportsLabel = intl.formatMessage({
    id: 'chart.chart_summary.energy_exports.label',
    defaultMessage: 'Energy Exports',
  });
  const tradedVolumeLabel = intl.formatMessage({
    id: 'chart.chart_summary.traded_volume.label',
    defaultMessage: 'Traded energy',
  });
  const energyUnitLabel = intl.formatMessage({
    id: 'chart.chart_summary.energy_cost_wh.abbr.label',
    defaultMessage: 'Wh',
  });
  const discountLabel = intl.formatMessage({
    id: 'chart.chart_summary.discount.label',
    defaultMessage: 'Discounted energy',
  });
  const discountAmountLabel = intl.formatMessage({
    id: 'chart.chart_summary.discount_amount.label',
    defaultMessage: 'Discount amount',
  });
  const untradedVolumeLabel = intl.formatMessage({
    id: 'chart.chart_summary.untraded_volume.label',
    defaultMessage: 'Untraded energy',
  });
  const averagePriceLabel = intl.formatMessage({
    id: 'chart.chart_summary.average_price.label',
    defaultMessage: 'Average price',
  });
  const tradedValueLabel = intl.formatMessage({
    id: 'chart.chart_summary.traded_value.label',
    defaultMessage: 'Traded value',
  });
  const untradedValueLabel = intl.formatMessage({
    id: 'chart.chart_summary.untraded_value.label',
    defaultMessage: 'Untraded value',
  });
  const carbonLabel = intl.formatMessage({
    id: 'chart.chart_summary.carbon.label',
    defaultMessage: 'Carbon',
  });

  // Currency formatting helper
  const intlCurrency = useIntlCurrency();
  const formatCurrencyValue = (n) => intlCurrency.formatMessage(
    { id: 'common.currency.value', defaultMessage: '{n, number, :: .00 currency/AUD}' },
    { n },
  );

  const finalTradedEnergy = (format('.4s')(tradedEnergy === undefined ? 0 : tradedEnergy));
  const netValueDiff = tradedValue - counterfactual;

  const direction = category === 'imports' ? 'TRADE_DIRECTION_BUY' : 'TRADE_DIRECTION_SELL';
  const absoluteValueDiff = Math.abs(netValueDiff);
  const diffState = netValueDiffState(netValueDiff, direction);
  const finalDiffState = diffState ? Object.keys(diffState).filter((k) => diffState[k])[0] : null;

  const title = category === 'imports' ? energyImportsLabel : energyExportsLabel;

  if (mode === PLATFORM_MODE_REBATE) {
    const finalRebatedEnergy = format('.4s')(rebatedEnergy === undefined ? 0 : rebatedEnergy);
    return (
      <Div category={category} align={align}>
        <Title>{title}</Title>
        <dl>
          <dt>{tradedVolumeLabel}</dt>
          <dd>
            {i18nDecimalFormat(finalTradedEnergy)}
            {energyUnitLabel}
          </dd>
          <dt>{discountLabel}</dt>
          <dd>
            {i18nDecimalFormat(finalRebatedEnergy)}
            {energyUnitLabel}
          </dd>
          <dt>{discountAmountLabel}</dt>
          <dd>
            {formatCurrencyValue(tradedValue)}
          </dd>
          {untradedEnergy > 0 && (
            <>
              <dt>{untradedVolumeLabel}</dt>
              <dd>
                {i18nDecimalFormat(format('.4s')(untradedEnergy))}
                {energyUnitLabel}
              </dd>
              {hasCounterfactual && untradedValue > 0 && (
                <>
                  <dt>{untradedValueLabel}</dt>
                  <dd>{formatCurrencyValue(untradedValue)}</dd>
                </>
              )}
            </>
          )}
        </dl>
      </Div>
    );
  }

  let averagePrice = 0;
  if (tradedValue !== undefined && tradedEnergy !== undefined && tradedEnergy !== 0) {
    averagePrice = tradedValue / tradedEnergy;
  }

  const counterfactualDetails = intl.formatMessage(
    {
      id: 'chart.chart_summary.platform_mode_trade.counterfactual.details',
      defaultMessage: ' {costBenefitAbbr}',
    },
    {
      costBenefitAbbr: (
        <abbr title={costBenefitLabel(intl, intlCurrency, absoluteValueDiff, finalDiffState)}>
          {format('.2f')(absoluteValueDiff)}
        </abbr>
      ),
    },
  );

  return (
    <Div category={category} align={align} className={`summary-${category}`}>
      <Title>{title}</Title>
      <dl>
        <dt>{averagePriceLabel}</dt>
        <dd>
          {valuePerUnit(intl, intlCurrency, averagePrice)}
        </dd>
        <dt>{tradedValueLabel}</dt>
        <dd>
          {formatCurrencyValue(tradedValue)}
          {hasCounterfactual && (
            <CostBenefitText>
              <span className="me-1">{COST_BENEFIT_STATE_ICONS[finalDiffState]}</span>
              {counterfactualDetails}
            </CostBenefitText>
          )}
        </dd>
        <dt>{tradedVolumeLabel}</dt>
        <dd>
          {i18nDecimalFormat(finalTradedEnergy)}
          {energyUnitLabel}
        </dd>
        {untradedEnergy > 0 && (
          <>
            {
              hasCounterfactual > 0 && (
                <>
                  <dt>{untradedValueLabel}</dt>
                  <dd>{formatCurrencyValue(untradedValue)}</dd>
                </>
              )
            }
            <dt>{untradedVolumeLabel}</dt>
            <dd>
              {i18nDecimalFormat(format('.4s')(untradedEnergy))}
              {energyUnitLabel}
            </dd>

          </>
        )}
        {
          !inhibitCarbonDataViews && carbonData > 0 && (
            <>
              <dt>{carbonLabel}</dt>
              <dd>
                {i18nDecimalFormat(getCarbonData(carbonData))}
              </dd>
            </>
          )
        }
      </dl>
    </Div>
  );
}

ChartSummary.propTypes = {
  category: PropTypes.oneOf(['imports', 'exports']).isRequired,
  hasCounterfactual: PropTypes.bool,
  tradedValue: PropTypes.number.isRequired,
  counterfactual: PropTypes.number.isRequired,
  tradedEnergy: PropTypes.number.isRequired,
  rebatedEnergy: PropTypes.number,
  align: PropTypes.oneOf(['left', 'right']),
  mode: PropTypes.string.isRequired,
  carbonData: PropTypes.oneOfType([
    PropTypes.instanceOf(Big),
    PropTypes.number,
  ]),
  untradedEnergy: PropTypes.number.isRequired,
  untradedValue: PropTypes.number.isRequired,
};

ChartSummary.defaultProps = {
  align: 'left',
  hasCounterfactual: false,
  rebatedEnergy: 0,
  carbonData: 0,
};

export default injectIntl(memo(ChartSummary));
